import { Interpolation, Theme } from '@emotion/react';
import { theme, ThemeConfig } from 'antd';
import { lightenDarkenColor } from '../utils';

const DARK_BLACK_PRIMARY = '#10161d';
const DARK_BLACK_SECONDARY = 'rgb(31, 41, 55)';
const DARK_BLACK_ELEVATED = 'rgb(20, 29, 41)';
const LIGHT_WHITE_PRIMARY = '#f3f4f6';
const LIGHT_WHITE_SECONDARY = 'white';
const LIGHT_BLACK_ELEVATED = '#fafbfc';
const INPUT_BG_COLOR = DARK_BLACK_PRIMARY;

const BORDER_COLORS_SUBTLE = {
  colorBorder: 'rgba(255, 255, 255, 0.12)',
  colorBorderSecondary: 'rgba(255, 255, 255, 0.08)',
  colorBorderBg: 'transparent',
};
const HEADER_HEIGHT = 40;
const BREADCRUMB_HEIGHT = 54;

export const decafThemeLight: ThemeConfig = {
  hashed: true,
  algorithm: [theme.defaultAlgorithm],
  components: {
    Layout: {
      headerHeight: HEADER_HEIGHT,
    },
    Tabs: {
      horizontalMargin: '0',
      horizontalItemPadding: '10px 12px',
      inkBarColor: '#3399FF',
    },
  },
  token: {
    borderRadius: 0,
    colorBgContainer: LIGHT_WHITE_SECONDARY,
    colorBgBase: LIGHT_WHITE_PRIMARY,
    colorBgLayout: LIGHT_WHITE_PRIMARY,
    colorBgElevated: LIGHT_BLACK_ELEVATED,
  },
};

export const decafThemeDark: ThemeConfig = {
  hashed: true,
  components: {
    Layout: {
      headerBg: DARK_BLACK_PRIMARY,
      headerHeight: HEADER_HEIGHT,
    },
    Button: {
      boxShadow: 'none',
      boxShadowSecondary: 'none',
      colorBgContainer: INPUT_BG_COLOR,
    },
    Input: {
      ...BORDER_COLORS_SUBTLE,
      colorBgContainer: INPUT_BG_COLOR,
    },
    Select: {
      ...BORDER_COLORS_SUBTLE,
      colorBgContainer: INPUT_BG_COLOR,
    },
    Checkbox: {
      ...BORDER_COLORS_SUBTLE,
      colorBgContainer: INPUT_BG_COLOR,
    },
    Dropdown: {
      ...BORDER_COLORS_SUBTLE,
    },
    DatePicker: {
      ...BORDER_COLORS_SUBTLE,
      colorBgContainer: INPUT_BG_COLOR,
    },
    InputNumber: {
      ...BORDER_COLORS_SUBTLE,
      colorBgContainer: INPUT_BG_COLOR,
    },
    Menu: {
      itemColor: 'rgba(255, 255, 255, 0.5)',
    },
    Tabs: {
      horizontalMargin: '0',
      horizontalItemPadding: '10px 12px',
      inkBarColor: '#3399FF',
      colorBorderSecondary: DARK_BLACK_ELEVATED,
    },
  },
  token: {
    fontFamily: 'Inter, sans-serif',
    colorPrimary: '#344961',
    colorBgBase: DARK_BLACK_SECONDARY,
    colorBgContainer: DARK_BLACK_PRIMARY,
    colorBgElevated: DARK_BLACK_ELEVATED,
    colorBorderSecondary: DARK_BLACK_SECONDARY,
    colorBorder: DARK_BLACK_SECONDARY,
    colorBgLayout: DARK_BLACK_SECONDARY,
    borderRadius: 0,
    green: '#48734d',
    red: '#b03a38',
    blue: '#0d6efd',
    yellow: '#ffc107',
    orange: '#fd7e14',
    colorWhite: '#fff',
    colorLink: '#a4bfff',
    colorLinkHover: '#7199fb',
    colorLinkActive: '#7199fb',
  },

  algorithm: [theme.darkAlgorithm],
};

function getBaseStyles(theme: ThemeConfig): Interpolation<Theme> {
  const menuOverride = {
    color: `white !important`,
    ':after': {
      borderBottomColor: 'white',
    },
  };

  return {
    body: {
      background: theme.token?.colorBgBase,
      fontFamily: 'Inter, sans-serif',
      margin: 0,
    },
    '#root': {
      minHeight: '100%',
    },
    button: {
      boxShadow: 'none !important',
    },
    '.ant-table-body': {
      overflow: 'auto !important',
    },
    '#decaf-header': {
      position: 'fixed',
      zIndex: 999,
      right: 0,
      left: 0,
      paddingInline: 16,
      display: 'flex',
      alignItems: 'center',
    },
    '#decaf-content': {
      paddingTop: decafThemeDark.components?.Layout?.headerHeight,
      paddingBottom: 32,
      '#decaf-layout-breadcrumb': {
        backgroundColor: theme.token?.colorBgElevated,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        height: BREADCRUMB_HEIGHT,
        paddingInline: 16,
        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.10)',
        ...breadcrumbStyles(),
      },
      '#decaf-layout-content-outlet': {
        minHeight: '100%',
        padding: 16,
        '&.full-screen': {
          padding: '0 !important',
        },
      },
    },
    '#decaf-footer': {
      position: 'fixed',
      bottom: 0,
      right: 0,
      left: 0,
      paddingBlock: 0,
      paddingInline: 0,
      zIndex: 999,
      boxShadow: '0px -2px 4px rgba(0, 0, 0, 0.15)',
      backgroundColor: decafThemeDark.components?.Layout?.headerBg,
    },
    '.dot': {
      borderRadius: '50%',
      width: 10,
      height: 10,
      display: 'inline-block',
      backgroundColor: 'rgba(255,255,255,.25)',
      '&.green': {
        backgroundColor: `#80ff00 !important`,
      },
      '&.yellow': {
        backgroundColor: `#ff0 !important`,
      },
      '&.orange': {
        backgroundColor: `#ff7000 !important`,
      },
      '&.red': {
        backgroundColor: `#ff0000 !important`,
      },
    },
    '.ant-menu-item-selected': menuOverride,
    '.ant-menu-light.ant-menu-horizontal >.ant-menu-item-selected': menuOverride,
    '.ant-menu-light.ant-menu-horizontal >.ant-menu-submenu-selected, .ant-menu-light .ant-menu-submenu-selected >.ant-menu-submenu-title':
      menuOverride,
  };
}

export function getLightStyles(theme: ThemeConfig): Interpolation<Theme> {
  const baseStyles = getBaseStyles(theme) as any;

  return {
    ...(baseStyles as any),
  };
}

export function getDarkStyles(theme: ThemeConfig): Interpolation<Theme> {
  const baseStyles = getBaseStyles(theme) as any;

  return {
    ...baseStyles,
    '*': {
      '&::-webkit-scrollbar': {
        width: 10,
        height: 10,
      },
      '&::-webkit-scrollbar-track': {
        background: theme.token?.colorBgContainer,
      },
      '&::-webkit-scrollbar-thumb': {
        background: theme.token?.colorPrimary,
      },
    },
    '.ant-page-header-back-button, .ant-page-header-heading-title': {
      color: `${theme.token?.colorWhite} !important`,
    },
    '.ant-badge-count': {
      color: `${theme.token?.colorWhite} !important`,
    },
    '.ant-table-thead > tr > th': {
      background: `${lightenDarkenColor(theme.token?.colorBgContainer || '', -5)} !important`,
      '&:hover': {
        background: `${theme.token?.colorBgContainer} !important`,
      },
    },
    '.ant-table-filter-dropdown': {
      'input, .ant-table-filter-dropdown-search-input': {
        background: `${theme.token?.colorBgBase} !important`,
      },
      '.ant-dropdown-menu-item, .ant-dropdown-menu': {
        background: `${theme.token?.colorBgContainer} !important`,
      },
    },
    '.ant-tabs-tab-active': {
      '.ant-tabs-tab-btn': {
        color: `${theme.token?.colorWhite} !important`,
      },
    },
    '.ant-radio-button-wrapper-checked': {
      backgroundColor: `${theme.components?.Button?.colorBgContainer} !important`,
      color: `${theme.token?.colorWhite} !important`,
    },
    '.ant-picker-date-panel': {
      '.ant-picker-cell-in-range, .ant-picker-cell-range-start, .ant-picker-cell-range-end': {
        '&::before': {
          background: `${theme.token?.colorBgBase} !important`,
        },
        '.ant-picker-cell-inner': {
          background: `${theme.token?.colorBgBase} !important`,
        },
      },
      '.ant-picker-cell-range-start, .ant-picker-cell-range-end': {
        '.ant-picker-cell-inner': {
          background: `${theme.token?.colorPrimary} !important`,
        },
      },
    },
  };
}

function breadcrumbStyles() {
  const getCrumbColor = (theme: 'dark' | 'light') => {
    const colorBase = theme === 'dark' ? '255, 255, 255' : '0, 0, 0';
    return {
      color: `rgba(${colorBase}, 0.5) !important`,
      '&:hover': {
        color: `rgba(${colorBase}, 0.75) !important`,
      },
    };
  };

  const darkColors = {
    ...getCrumbColor('dark'),
    '&:last-child': {
      color: 'rgba(255, 255, 255, 0.85) !important',
    },
  };

  const lightColors = {
    ...getCrumbColor('light'),
    '&:last-child': {
      color: 'rgba(0, 0, 0, 0.85) !important',
    },
  };

  return {
    '.decaf-breadcrumb': {
      display: 'flex',
      alignItems: 'center',
      listStyle: 'none',
      margin: 0,
      padding: 0,
      '>li': {
        '&.separator': {
          paddingInline: 3,
          marginInline: 3,
        },
        'a, span': {
          ...getCrumbColor('light'),
        },
        display: 'flex',
        alignItems: 'center',
        transition: 'color 0.2s',
        ...lightColors,
      },
      '&.dark': {
        '>li': {
          'a, span': {
            ...getCrumbColor('dark'),
          },
          ...darkColors,
        },
      },
    },
  };
}
